import React, { useCallback, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Container from '@common/components/container';
import { TopNavigationBar } from '@common/components/navigation-bar';
import { Button } from '@common/components/button';
import { useHistory, useParams } from 'react-router';
import Card from '@common/components/card';
import { CardContent } from '@common/components/card/card-content';
import { DndProvider } from 'react-dnd';
import dndManager from '@common/utils/dnd-manager';
import { useAppSelector } from '@common/hooks/redux/use-app-selector';
import { useThunkDispatch } from '@common/hooks/redux/use-thunk-dispatch';
import { AlertService } from '@common/services/alert';
import { transformGeneratedOutline } from '../../utils/transform-outline';
import GenerateCourseModal from '@modules/learning/components/generate-course-modal/generate-course-model';
import createFromOutline from '../../actions/create-from-outline';
import QuizModuleComponent from '@modules/learning/components/quiz-module';
import { DialogWrapper } from '@common/components/modal/dialog-wrapper';
import { TextareaInputHookForm as TextareaInput } from '@common/components/form/inputs/textarea/textarea';
import { Row, Group, FormLabelHookForm } from '@common/components/form';
import { ConfirmButton } from '@common/components/confirm-button';
import { useCourseOutlineForm } from './hooks/use-course-outline-form';
import ModuleItem from './components/module-item';
import SectionHeader from './components/section-header';
import {
  getQuizModulesFromOutline,
  createCourseOutlinePayload,
} from './utils/course-outline-utils';
import { ECourseTypes } from '@modules/learning/definitions';
import useUnsavedChanges from '@common/hooks/use-unsaved';

const CourseOutlineContainerComponent: React.FC = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const dispatch = useThunkDispatch();
  const params = useParams<{ type: ECourseTypes }>();

  const generatedOutline = useAppSelector(
    (state) => state.learning?.generatedOutline?.outline
  );
  const generationSettings = useAppSelector(
    (state) => state.learning?.generatedOutline?.settings
  );

  const transformedOutline = generatedOutline
    ? transformGeneratedOutline({
        ...generatedOutline,
        modules: generatedOutline.modules.filter(
          (module) => module.type === 'content'
        ),
      })
    : null;

  const quizData = getQuizModulesFromOutline(generatedOutline);

  const [isCreatingCourse, setIsCreatingCourse] = useState<boolean>(false);
  const [showEditSectionModal, setShowEditSectionModal] =
    useState<boolean>(false);
  const [showEditQuizModal, setShowEditQuizModal] = useState<boolean>(false);
  const [editingQuizIndex, setEditingQuizIndex] = useState<number | null>(null);
  const [showContentSection, setShowContentSection] = useState<boolean>(true);

  const {
    control,
    sectionControl,
    quizControl,
    handleSectionSubmit,
    handleQuizSubmit,
    resetSectionForm,
    resetQuizForm,
    updateModule,
    updateLesson,
    deleteModule,
    deleteLesson,
    updateCourseTitle,
    updateCourseDescription,
    updateQuizModule,
    deleteQuizModule,
    watch,
    setValue,
  } = useCourseOutlineForm({
    initialCourseTitle: transformedOutline?.title || '',
    initialDescription: transformedOutline?.description || '',
    initialModules: transformedOutline?.modules || [],
    initialQuizModules: quizData,
  });

  const formValues = watch();
  const { courseTitle, courseDescription, modules, quizModules } = formValues;

  useEffect(() => {
    if (transformedOutline) {
      updateCourseTitle(transformedOutline.title);
      updateCourseDescription(transformedOutline.description);
    }
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const { handleConfirmNavigation, handleCancelNavigation } = useUnsavedChanges(
    {
      isDirty: true,
    }
  );

  const handleEditSectionClick = () => {
    resetSectionForm({ courseTitle, courseDescription });
    setShowEditSectionModal(true);
  };

  const handleCloseSectionModal = () => {
    setShowEditSectionModal(false);
  };

  const onSectionSubmit = (data: { courseTitle: string, courseDescription: string }) => {
    updateCourseTitle(data.courseTitle);
    updateCourseDescription(data.courseDescription);
    handleCloseSectionModal();
  };

  const handleEditQuizClick = (quizIndex: number) => {
    const quiz = quizModules[quizIndex];
    setEditingQuizIndex(quizIndex);
    resetQuizForm({ title: quiz.title });
    setShowEditQuizModal(true);
  };

  const handleCloseQuizModal = () => {
    setShowEditQuizModal(false);
    setEditingQuizIndex(null);
  };

  const onQuizSubmit = (data: { title: string }) => {
    if (editingQuizIndex !== null) {
      updateQuizModule(editingQuizIndex, data.title);
    }
    handleCloseQuizModal();
  };

  const handleDeleteQuiz = (quizIndex: number) => {
    deleteQuizModule(quizIndex);
    AlertService.success(t('learning:course_outline_quiz_deleted'));
  };

  const handleDeleteSection = () => {
    setShowContentSection(false);
    setValue('modules', []);
    AlertService.success(t('learning:course_outline_content_section_deleted'));
  };

  const handleOpenCourse = useCallback(async () => {
    try {
      setIsCreatingCourse(true);

      const modifiedOutline = createCourseOutlinePayload(
        courseTitle,
        courseDescription,
        modules,
        quizModules,
        formValues
      );

      const result = await dispatch(
        createFromOutline(generationSettings, modifiedOutline)
      );

      if (result) {
        AlertService.success(t('learning:form_course_created'));
        history.push(`/admin/learning/courses/${result.item.course.id}`);
      }
    } catch (error) {
      AlertService.error(t('learning:course_outline_create_course_failed'));
    } finally {
      setIsCreatingCourse(false);
    }
  }, [
    dispatch,
    generatedOutline,
    generationSettings,
    courseTitle,
    modules,
    quizModules,
    formValues,
    t,
    params.type,
  ]);

  if (!transformedOutline) {
    return (
      <Container name="GenAI" className="LearningNewSection">
        <TopNavigationBar
          breadcrumbs={[
            { name: t('learning:breadcrumb_learning_environment') },
            params.type === ECourseTypes.ONBOARDING
              ? {
                  name: t('learning:breadcrumb_onboarding'),
                  path: '/admin/learning/onboarding',
                }
              : {
                  name: t('learning:breadcrumb_academy'),
                  path: '/admin/learning/academy',
                },
            { name: t('learning:course_outline_breadcrumb_generate_ai') },
            { name: t('learning:course_outline_breadcrumb_title') },
          ]}
          title={t('learning:course_outline_title')}
          action={
            <>
              <Button
                className="!tw-ml-[10px]"
                onClick={() => history.push(`/admin/learning/${params.type}`)}
              >
                {t('forms:go_back')}
              </Button>
              <Button
                className="!tw-ml-[10px]"
                type="primary"
                onClick={() => {
                  history.push(`/admin/learning/${params.type}/course/generate`, {
                    generationSettings
                  });
                }}
              >
                {t('learning:course_outline_edit_prompt')}
              </Button>
              <GenerateCourseModal isCreatingCourse={isCreatingCourse}>
                <Button
                  type="primary"
                  disabled={isCreatingCourse}
                  onClick={handleOpenCourse}
                >
                  {t('learning:course_outline_generate_course')}
                </Button>
              </GenerateCourseModal>
            </>
          }
        />
        <Container.Content>
          <Card containerClassName="tw-w-full">
            <CardContent className="!tw-w-[500px] !tw-w-[90%] tw-flex tw-flex-col tw-gap-5 !tw-p-[32px]">
              <div className="tw-w-full tw-mx-auto tw-p-6">
                <div className="tw-text-center tw-text-gray-500 tw-text-lg">
                  {t('learning:course_outline_no_outline_message')}
                </div>
              </div>
            </CardContent>
          </Card>
        </Container.Content>
      </Container>
    );
  }

  return (
    <>
      <Container name="GenAI" className="LearningNewSection">
        <TopNavigationBar
          breadcrumbs={[
            { name: t('learning:breadcrumb_learning_environment') },
            params.type === ECourseTypes.ONBOARDING
              ? {
                  name: t('learning:breadcrumb_onboarding'),
                  path: '/admin/learning/onboarding',
                }
              : {
                  name: t('learning:breadcrumb_academy'),
                  path: '/admin/learning/academy',
                },
            { name: t('learning:course_outline_breadcrumb_generate_ai') },
            { name: t('learning:course_outline_breadcrumb_title') },
          ]}
          title={t('learning:course_outline_title')}
          action={
            <>
              <ConfirmButton
                title={t('learning:gen_ai_unsaved_changes_title')}
                description={t('learning:gen_ai_unsaved_changes_description')}
                onCancel={handleCancelNavigation}
                onConfirm={() => handleConfirmNavigation(`/admin/learning/${params.type}`)}
                confirmText={t('learning:gen_ai_unsaved_changes_confirm')}
                cancelText={t('learning:gen_ai_unsaved_changes_cancel')}
                danger
                showGlobalModal={false}
              >
                <Button className="!tw-ml-[10px]">
                  {t('forms:go_back')}
                </Button>
              </ConfirmButton>
              <Button
                type="primary"
                className="!tw-ml-[10px]"
                onClick={() => {
                  history.push(`/admin/learning/${params.type}/course/generate`, {
                    generationSettings
                  });
                }}
              >
                {t('learning:course_outline_edit_prompt')}
              </Button>
              <GenerateCourseModal isCreatingCourse={isCreatingCourse}>
                <Button
                  type="primary"
                  disabled={isCreatingCourse}
                  isLoading={isCreatingCourse}
                  onClick={handleOpenCourse}
                >
                  {isCreatingCourse
                    ? t('learning:course_outline_creating_course')
                    : t('learning:course_outline_generate_course')}
                </Button>
              </GenerateCourseModal>
            </>
          }
        />
        <Container.Content className="tw-flex tw-flex-col tw-gap-4">
          {showContentSection && (
            <Card>
              <CardContent className="!tw-w-[500px] !tw-w-[95%] tw-flex tw-flex-col tw-gap-5 !tw-p-[32px]">
                <div className="tw-w-full tw-mx-auto">
                  <SectionHeader
                    courseTitle={courseTitle}
                    courseDescription={courseDescription}
                    onEditClick={handleEditSectionClick}
                    onDeleteClick={handleDeleteSection}
                  />
                  <div className="tw-space-y-4">
                    <DndProvider manager={dndManager}>
                      {modules.map((module, index) => (
                        <ModuleItem
                          module={module}
                          index={index}
                          onDeleteModule={deleteModule}
                          onDeleteLesson={deleteLesson}
                          onUpdateModule={updateModule}
                          onUpdateLesson={updateLesson}
                        />
                      ))}
                      {quizModules.map((quizModule, index) => (
                        <QuizModuleComponent
                          quizModule={quizModule}
                          index={index}
                          control={control}
                          onEdit={() => handleEditQuizClick(index)}
                          onDelete={() => handleDeleteQuiz(index)}
                        />
                      ))}
                    </DndProvider>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </Container.Content>
      </Container>

      {/* Edit Section Modal */}
      <DialogWrapper
        show={showEditSectionModal}
        onHide={handleCloseSectionModal}
        size="small"
        className="EditableModule"
        title={t('learning:course_outline_edit_section_title')}
        onConfirm={handleSectionSubmit(onSectionSubmit)}
        confirmButtonText={t('learning:course_outline_save_changes')}
        content={
          <>
            <Row>
              <Group>
                <FormLabelHookForm
                  control={sectionControl}
                  name="courseTitle"
                  text={t('learning:learning_module_heading_label')}
                  className="!tw-text-[13px] !tw-font-inter !tw-font-normal !tw-text-gray-text"
                />
                <TextareaInput
                  className="!tw-h-[100px] tw-text-base tw-font-inter tw-font-medium"
                  name="courseTitle"
                  control={sectionControl}
                  placeholder={t('learning:course_outline_enter_section_title')}
                  minRows={3}
                />
              </Group>
            </Row>
            <Row>
              <Group>
                <FormLabelHookForm
                  control={sectionControl}
                  name="courseDescription"
                  text={t('learning:learning_module_description_label')}
                  className="!tw-text-[13px] !tw-font-inter !tw-font-normal !tw-text-gray-text"
                />
                <TextareaInput
                  className="!tw-h-[200px] tw-text-base tw-font-inter tw-font-medium"
                  name="courseDescription"
                  control={sectionControl}
                  placeholder={t('learning:learning_module_enter_objectives')}
                  minRows={8}
                />
              </Group>
            </Row>
          </>
        }
      />

      {/* Edit Quiz Modal */}
      <DialogWrapper
        show={showEditQuizModal}
        onHide={handleCloseQuizModal}
        size="small"
        className="EditableModule"
        title={t('learning:course_outline_edit_quiz_title')}
        onConfirm={handleQuizSubmit(onQuizSubmit)}
        confirmButtonText={t('learning:course_outline_save_changes')}
        content={
          <Row>
            <Group>
              <TextareaInput
                className="!tw-h-[100px] tw-text-base tw-font-inter tw-font-medium"
                name="title"
                control={quizControl}
                placeholder={t('learning:course_outline_enter_quiz_title')}
                minRows={3}
              />
            </Group>
          </Row>
        }
      />
    </>
  );
};

export default CourseOutlineContainerComponent;
